import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import BranchManagerFields from "../components/BranchManagerFields";
import WorkingHoursEditor, {
  emptyWeek,
  openDayCount,
  toRows,
} from "../components/WorkingHoursEditor";
import BranchLocationFields from "../components/BranchLocationFields";
import BranchGenderField from "../components/BranchGenderField";
import BusinessTypePicker from "../components/BusinessTypePicker";
import GooglePlaceCard from "../components/GooglePlaceCard";
import SearchableSelect from "../components/SearchableSelect";
import useBranchUsage from "../hooks/useBranchUsage";
import useBusinessGender from "../hooks/useBusinessGender";
import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { EMPTY_MANAGER, MANAGER_FIELD_IDS, validateManager } from "../services/branchManager";
import { focusFirstError } from "../services/formFocus";
import { getCurrentUser } from "../services/authService";
import { activeLimitReached, createBranch } from "../services/branchService";
import { hasFeature } from "../services/features";
import { TIMEZONES } from "../services/timezones";
import {
  fetchBusinessTypes,
  fetchBusinessUsers,
  fetchCities,
  fetchCountries,
  fetchCurrencies,
} from "../services/lookupService";

const EMPTY = {
  name: "",
  code: "",
  status: "active",
  // male | female, chosen only when the business serves both.
  gender: "",
  description: "",
  address: "",
  addressLine2: "",
  region: "",
  postalCode: "",
  phone: "",
  email: "",
  capacity: "",
  // Blank follows the city's country; the picker offers the common zones.
  timezone: "",
  latitude: "",
  longitude: "",
  // A pasted Google Maps link. Optional, and beside the coordinates rather
  // than instead of them.
  mapUrl: "",
  // Optional Google place id or link; blank lets the API read the map link.
  googlePlaceId: "",
};

export default function CreateBranch() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getCurrentUser();
  const businessCountryId = user?.business?.country_id;

  const [values, setValues] = useState(EMPTY);
  const [countryId, setCountryId] = useState("");
  const [cityId, setCityId] = useState("");
  const [currencyId, setCurrencyId] = useState("");
  const [businessTypeIds, setBusinessTypeIds] = useState([]);
  const [logo, setLogo] = useState(null);
  const [media, setMedia] = useState([]);
  const [manager, setManager] = useState(EMPTY_MANAGER);
  // Hours are part of creating a branch now, not a later step. Nothing is open
  // to begin with, so the form starts invalid on purpose.
  const [week, setWeek] = useState(emptyWeek);

  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [users, setUsers] = useState([]);
  // OUP-26 — a failed lookup must not read as "this business has no users".
  const [usersFailed, setUsersFailed] = useState(false);

  const [loadingLookups, setLoadingLookups] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const { allowedGenders, loading: loadingGender } = useBusinessGender();
  const genderChoice = allowedGenders.length > 1;

  // The plan caps active branches, not branches: at the limit a new branch can
  // still be opened, only as a draft.
  const { usage } = useBranchUsage();
  const activeLocked = activeLimitReached(usage);

  useEffect(() => {
    if (activeLocked) setValues((v) => (v.status === "active" ? { ...v, status: "draft" } : v));
  }, [activeLocked]);

  // Country-independent lists. The country defaults to the one the business is
  // registered in, which is also the only one the API accepts cities from.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const [countryList, currencyList, typeList] = await Promise.all([
          fetchCountries(),
          fetchCurrencies(),
          fetchBusinessTypes(),
        ]);

        if (cancelled) return;
        setCountries(countryList);
        setCurrencies(currencyList);
        setBusinessTypes(typeList);
        if (businessCountryId) setCountryId(String(businessCountryId));
      } catch (err) {
        if (!cancelled) setBanner(apiErrorMessage(err, t("branchForm.loadFailed")));
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }

      // OUP-26 — asked for on its own. Inside the Promise.all above, a 403 from
      // /business/users (it needs `users.view`) rejected the whole batch and
      // left the form with no countries, no currencies and no types either.
      try {
        const userList = await fetchBusinessUsers();
        if (!cancelled) {
          setUsers(userList);
          setUsersFailed(false);
        }
      } catch {
        if (!cancelled) {
          setUsers([]);
          setUsersFailed(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [businessCountryId, i18n.language, t]);

  /**
   * OUP-4, narrowed by OUP-38 — the currency follows the country, and is no
   * longer overridable. The earlier decision left it changeable for a business
   * pricing across a border; product reversed that, and the API now refuses a
   * currency that is not the country's, so an editable field could only ever
   * produce a 422.
   *
   * Cleared, not left standing, when a country carries none: showing the
   * previous country's currency beside a new country would be a lie, and the
   * server would refuse it anyway.
   */
  useEffect(() => {
    if (!countryId) return;

    const country = countries.find((c) => String(c.id) === String(countryId));
    setCurrencyId(country?.currency?.id ? String(country.currency.id) : "");
  }, [countryId, countries]);

  // Cities follow the selected country.
  useEffect(() => {
    let cancelled = false;

    if (!countryId) {
      setCities([]);
      return undefined;
    }

    setLoadingCities(true);
    setCityId("");

    (async () => {
      try {
        const list = await fetchCities(countryId);
        if (!cancelled) setCities(list);
      } catch (err) {
        if (!cancelled) {
          setCities([]);
          setBanner(apiErrorMessage(err, t("branchForm.citiesFailed")));
        }
      } finally {
        if (!cancelled) setLoadingCities(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [countryId, i18n.language, t]);

  const set = (field) => (e) => setValues((v) => ({ ...v, [field]: e.target.value }));
  const fieldError = (name) => errors[name]?.[0];

  /**
   * OUP-2 — API error key -> the id of the input that owns it, in the order the
   * fields appear on the page, so a failed submit sends the user to the first
   * one they can see rather than the first one Laravel happened to report.
   */
  const FIELD_IDS = {
    name: "name",
    city_id: "cityId",
    currency_id: "currencyId",
    business_type_ids: "businessTypeIds",
    gender: "gender",
    ...MANAGER_FIELD_IDS,
    code: "code",
    status: "status",
    address: "address",
    address_line2: "addressLine2",
    region: "region",
    postal_code: "postalCode",
    phone: "phone",
    email: "email",
    capacity: "capacity",
    timezone: "timezone",
    description: "description",
    map_url: "mapUrl",
    google_place_id: "googlePlaceId",
    working_hours: "working-hours",
    logo: "logo",
    media: "media",
  };

  /** Show the errors, then take the user to the first field that has one. */
  const reportErrors = (found) => {
    setErrors(found);
    focusFirstError(found, FIELD_IDS);
  };

  // The API reports list errors as media.0, business_type_ids.0 … — show the first.
  const firstErrorFor = (prefix) =>
    Object.entries(errors).find(([key]) => key.startsWith(prefix))?.[1]?.[0];

  const countryOptions = useMemo(
    () => countries.map((c) => ({ value: c.id, label: `${c.flag ?? ""} ${c.name}`.trim() })),
    [countries]
  );
  const cityOptions = useMemo(() => cities.map((c) => ({ value: c.id, label: c.name })), [cities]);
  const currencyOptions = useMemo(
    () => currencies.map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` })),
    [currencies]
  );

  // What the locked field shows: the chosen currency's own label, so it reads
  // the same as it did when this was a picker.
  const currencyLabel =
    currencyOptions.find((o) => String(o.value) === String(currencyId))?.label ?? "";

  const openDays = openDayCount(week);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBanner("");

    const found = validateManager(manager, t);

    // The coordinates are no longer asked for — they are read from the map
    // link, which is what create demands in their place.
    if (!String(values.mapUrl ?? "").trim()) {
      found.map_url = [t("branchForm.errors.mapUrl")];
    }
    if (!String(values.address ?? "").trim()) {
      found.address = [t("branchForm.errors.address")];
    }
    if (!values.name.trim()) found.name = [t("branchForm.errors.name")];
    if (!cityId) found.city_id = [t("branchForm.errors.city")];
    // OUP-38 — nothing to fix by hand any more: an empty currency means the
    // chosen country has none on file, so the message points at the country.
    if (!currencyId) found.currency_id = [t("branchForm.errors.currencyCountry")];
    if (businessTypeIds.length === 0) {
      found.business_type_ids = [t("branchForm.errors.businessTypes")];
    }
    if (genderChoice && !values.gender) found.gender = [t("branchForm.genderRequired")];

    if (Object.keys(found).length > 0) {
      reportErrors(found);
      setBanner(t("branchForm.errors.summary"));
      return;
    }

    // Mirrors the API's rule so an obvious slip costs no round trip. The API
    // answers 422 on `working_hours` for the same case.
    if (openDays === 0) {
      reportErrors({ working_hours: [t("workingHours.errors.required")] });
      setBanner(t("workingHours.errors.required"));
      return;
    }

    setErrors({});
    setSubmitting(true);

    try {
      const branch = await createBranch({
        ...values,
        cityId,
        currencyId,
        businessTypeIds,
        workingHours: toRows(week),
        logo,
        media,
        manager,
      });
      navigate("/branches", {
        state: { flash: t("branchForm.created", { name: branch.name }) },
      });
    } catch (err) {
      reportErrors(apiFieldErrors(err));
      setBanner(apiErrorMessage(err, t("branchForm.createFailed")));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{t("branchForm.title")}</h1>
          <p className="text-sm text-ink-2">
            {t("branchForm.subtitle", { business: user?.business?.name ?? "" })}
          </p>
        </div>
        <button type="button" onClick={() => navigate("/branches")} className="btn-secondary">
          {t("common.back")}
        </button>
      </div>

      {banner && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {banner}
        </div>
      )}

      <form onSubmit={handleSubmit} className="card p-5" noValidate>
        <fieldset disabled={loadingLookups || submitting} className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="field-label" htmlFor="name">
              {t("branchForm.name")} <span className="text-danger-text">*</span>
            </label>
            <input id="name" className="field-input" value={values.name} onChange={set("name")} />
            {fieldError("name") && <p className="field-error">{fieldError("name")}</p>}
          </div>

          <SearchableSelect
            id="countryId"
            label={t("branchForm.country")}
            required
            options={countryOptions}
            value={countryId}
            onChange={setCountryId}
            placeholder={t("branchForm.selectCountry")}
          />

          <SearchableSelect
            id="cityId"
            label={t("branchForm.city")}
            required
            options={cityOptions}
            value={cityId}
            onChange={setCityId}
            placeholder={loadingCities ? t("branchForm.loadingCities") : t("branchForm.selectCity")}
            disabled={!countryId || loadingCities}
            error={fieldError("city_id")}
            emptyText={t("branchForm.noCities")}
          />

          {/* OUP-38 — read-only. It is the country's currency or nothing, so
              this reports the answer rather than asking the question. Not a
              disabled <select>: there is nothing to choose between. */}
          <div>
            <label className="field-label" htmlFor="currencyId">
              {t("branchForm.currency")}
            </label>
            <input
              id="currencyId"
              type="text"
              readOnly
              dir="ltr"
              className="field-input cursor-not-allowed bg-raised text-ink-2"
              value={currencyLabel}
              placeholder={t("branchForm.currencyFromCountry")}
            />
            <p className="mt-1 text-xs text-ink-2">{t("branchForm.currencyHint")}</p>
            {/* Without multi_currency the API refuses (422 currency_id) a currency
                the other branches do not use; say so before the save. */}
            {!hasFeature(user, "multi_currency") && (
              <p className="mt-1 text-xs text-ink-2">{t("plan.currencyOff")}</p>
            )}
            {fieldError("currency_id") && (
              <p className="field-error">{fieldError("currency_id")}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <BusinessTypePicker
              businessTypes={businessTypes}
              selected={businessTypeIds}
              onChange={setBusinessTypeIds}
              error={firstErrorFor("business_type_ids")}
            />
          </div>

          <BranchGenderField
            allowedGenders={allowedGenders}
            value={values.gender}
            onChange={(gender) => setValues((v) => ({ ...v, gender }))}
            error={fieldError("gender")}
            loading={loadingGender}
          />

          <BranchManagerFields
            value={manager}
            onChange={setManager}
            users={users}
            errors={errors}
            loading={loadingLookups}
            usersFailed={usersFailed}
          />

          <BranchLocationFields
            values={values}
            onChange={setValues}
            errors={errors}
            timezones={TIMEZONES}
            activeLockedHint={
              activeLocked ? t("branchForm.activeLocked", { limit: usage.branches_active_limit }) : ""
            }
            required
          />

          <GooglePlaceCard
            value={values.googlePlaceId}
            onChange={(googlePlaceId) => setValues((v) => ({ ...v, googlePlaceId }))}
            error={errors.google_place_id?.[0]}
            searchQuery={[
              user?.business?.name,
              values.name.trim(),
              cities.find((city) => String(city.id) === String(cityId))?.name,
            ]
              .filter(Boolean)
              .join(" ")}
            lat={values.latitude}
            lng={values.longitude}
          />

          <div className="md:col-span-2">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              {/* Not a <label>: the control below is a seven-row grid, not one input. */}
              <p className="field-label mb-0">
                {t("workingHours.title")} <span className="text-danger-text">*</span>
                <span className="ms-2 rounded-full bg-raised px-2 py-1 text-xs font-normal text-ink-2">
                  {t("workingHours.days", { open: openDays })}
                </span>
              </p>
            </div>

            <p className="mb-2 text-xs text-ink-2">{t("workingHours.createHint")}</p>

            <div id="working-hours" tabIndex={-1} className="rounded-lg border border-line px-3">
              <WorkingHoursEditor week={week} onChange={setWeek} disabled={submitting} errors={errors} />
            </div>

            {fieldError("working_hours") && (
              <p className="field-error">{fieldError("working_hours")}</p>
            )}
          </div>

          <div>
            <label className="field-label" htmlFor="logo">
              {t("branchForm.logo")}
            </label>
            <input
              id="logo"
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              className="field-input"
              onChange={(e) => setLogo(e.target.files[0] ?? null)}
            />
            <p className="mt-1 text-xs text-ink-2">{t("branchForm.logoHint")}</p>
            {fieldError("logo") && <p className="field-error">{fieldError("logo")}</p>}
          </div>

          <div>
            <label className="field-label" htmlFor="media">
              {t("branchForm.media")}
            </label>
            <input
              id="media"
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.webp,.mp4,.mov,.webm"
              className="field-input"
              onChange={(e) => setMedia(Array.from(e.target.files))}
            />
            <p className="mt-1 text-xs text-ink-2">
              {t("branchForm.mediaHint")}
              {media.length > 0 && ` ${t("branchForm.filesSelected", { count: media.length })}`}
            </p>
            {firstErrorFor("media") && <p className="field-error">{firstErrorFor("media")}</p>}
          </div>
        </fieldset>

        <div className="mt-6 flex gap-2 border-t border-line pt-5">
          <button
            type="submit"
            disabled={submitting || loadingLookups || openDays === 0}
            title={openDays === 0 ? t("workingHours.errors.required") : undefined}
            className="btn-primary"
          >
            {submitting ? t("branchForm.submitting") : t("branchForm.submit")}
          </button>
          <button type="button" onClick={() => navigate("/branches")} className="btn-secondary">
            {t("common.cancel")}
          </button>
        </div>
      </form>
    </div>
  );
}
